import { FC, ReactNode, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { FloorChips } from 'src/components/FloorChips/FloorChips';
import { FloorMap } from 'src/components/FloorMap/FloorMap';
import { useFloorMap } from 'src/components/FloorMap/FloorMapProvider';
import { RenderBookingObjectParams } from 'src/components/FloorMap/types';
import { fullscreenIsSupported } from 'src/consts';
import { getAgentState, getIsDesktop } from 'src/store/agent/selectors';
import { getDesksState, getFilteredDesks } from 'src/store/desks/selectors';
import { openDesk } from 'src/store/desks/slice';
import { useAppDispatch } from 'src/store/useAppDispatch';
import { BookingObject, PlaceType } from 'src/types';

import { useFloorChips } from '../../components/FloorChips/FloorChipsContext';

import { BookingDeskPlace } from './BookingDeskPlace/BookingDeskPlace';
import { RoomPlace } from './RoomPlace/RoomPlace';

export const renderBookingObject = ({
  bookingObject,
}: RenderBookingObjectParams<BookingObject>): ReactNode => {
  if (bookingObject.type === PlaceType.Desk)
    return (
      <BookingDeskPlace key={bookingObject.mapPlaceId} item={bookingObject} />
    );

  if (bookingObject.type === PlaceType.Room)
    return <RoomPlace key={bookingObject.mapPlaceId} item={bookingObject} />;

  return null;
};

export const DeskBookingFloorMap: FC = () => {
  const { touch } = useSelector(getAgentState);
  const isDesktop = useSelector(getIsDesktop);
  const dispatch = useAppDispatch();
  const { scrollTo } = useFloorMap();
  const { locationLoading, loading, staticObjects, bookingObjects } =
    useSelector(getDesksState);
  const filteredBookingObjects = useSelector(getFilteredDesks);

  const geoObjects = useMemo((): Map<string, BookingObject> => {
    return new Map([...bookingObjects, ...staticObjects]);
  }, [bookingObjects, staticObjects]);

  const { activeFloor, setActiveFloor, items } = useFloorChips();

  const chips = useMemo((): ReactNode => {
    return (
      <FloorChips
        activeFloor={activeFloor}
        setActiveFloor={setActiveFloor}
        floors={items}
        testId='deskBookingFloors'
      />
    );
  }, [activeFloor, items, setActiveFloor]);

  const book = useCallback(
    async (id: string) => {
      const geoObject = filteredBookingObjects.get(id);

      if (!geoObject || geoObject.type !== PlaceType.Desk) return;

      dispatch(openDesk(id));

      if (isDesktop) scrollTo(geoObject);
    },
    [dispatch, filteredBookingObjects, isDesktop, scrollTo],
  );

  return (
    <FloorMap
      loading={loading || locationLoading}
      bookingObjects={geoObjects}
      onClickOnBookingObject={book}
      footer={isDesktop ? chips : null}
      renderBookingObject={renderBookingObject}
      touchDevice={touch}
      fullscreenIsSupported={fullscreenIsSupported && isDesktop}
    />
  );
};
